/**
 * Text selection helpers for the message composer (markdown-style formatting).
 */

export const MESSAGE_FONTS = [
  "Arial",
  "Georgia",
  "Times New Roman",
  "Verdana",
  "Courier New",
  "Trebuchet MS",
];

export const MESSAGE_FONT_SIZES = [12, 14, 16, 18, 20, 24];

export const MESSAGE_LINE_SPACINGS = [
  { value: "1", label: "Single" },
  { value: "1.15", label: "1.15" },
  { value: "1.5", label: "1.5" },
  { value: "2", label: "Double" },
];

export const MESSAGE_TEXT_COLORS = [
  "#000000",
  "#333333",
  "#666666",
  "#025feb",
  "#c62828",
  "#2e7d32",
  "#ef6c00",
  "#6a1b9a",
  "#00838f",
  "#ad1457",
];

export const MESSAGE_HIGHLIGHT_COLORS = [
  "#fff59d",
  "#ffcc80",
  "#ef9a9a",
  "#ce93d8",
  "#90caf9",
  "#a5d6a7",
  "#80deea",
  "#f8bbd0",
  "#e0e0e0",
  "#ffffff",
];

export function wrapTextSelection(textarea, before, after, defaultText = "text") {
  return wrapRange(
    textarea.value,
    textarea.selectionStart,
    textarea.selectionEnd,
    before,
    after,
    defaultText
  );
}

export function wrapRange(value, start, end, before, after, defaultText = "text") {
  const selected = value.slice(start, end) || defaultText;
  const newValue = value.slice(0, start) + before + selected + after + value.slice(end);
  const selectStart = start + before.length;
  const selectEnd = selectStart + selected.length;
  return { newValue, selectStart, selectEnd };
}

export function wrapWithMarkers(textarea, openTag, closeTag, defaultText = "text") {
  return wrapTextSelection(textarea, openTag, closeTag, defaultText);
}

export function wrapRangeWithMarkers(value, start, end, openTag, closeTag, defaultText = "text") {
  return wrapRange(value, start, end, openTag, closeTag, defaultText);
}

export function insertLinkAtSelection(value, start, end, text, url) {
  const displayText = (text || "").trim() || url.trim();
  const normalizedUrl = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
  const linkMarkdown = `[${displayText}](${normalizedUrl})`;
  const newValue = value.slice(0, start) + linkMarkdown + value.slice(end);
  const cursor = start + linkMarkdown.length;
  return { newValue, selectStart: cursor, selectEnd: cursor };
}

export function insertAtCursor(textarea, text) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;
  const newValue = value.slice(0, start) + text + value.slice(end);
  const cursor = start + text.length;
  return { newValue, selectStart: cursor, selectEnd: cursor };
}

export function applyLinePrefix(textarea, prefixFn) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;

  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = value.indexOf("\n", end);
  const blockEnd = lineEnd === -1 ? value.length : lineEnd;
  const block = value.slice(lineStart, blockEnd);

  const lines = block.split("\n");
  const prefixed = lines
    .map((line, i) => {
      if (!line.trim()) return line;
      return prefixFn(line, i);
    })
    .join("\n");

  const newValue = value.slice(0, lineStart) + prefixed + value.slice(blockEnd);
  return { newValue, selectStart: lineStart, selectEnd: lineStart + prefixed.length };
}

const INDENT_UNIT = "  ";

export function increaseLineIndent(textarea) {
  return applyLinePrefix(textarea, (line) => `${INDENT_UNIT}${line}`);
}

export function decreaseLineIndent(textarea) {
  return applyLinePrefix(textarea, (line) =>
    line.startsWith(INDENT_UNIT) ? line.slice(INDENT_UNIT.length) : line.replace(/^\t/, "")
  );
}

export function applyFontAndSize(textarea, fontFamily, fontSize) {
  const open = `{{f:${fontFamily}|${fontSize}}}`;
  return wrapWithMarkers(textarea, open, "{{/f}}");
}

export function applyFontAndSizeToRange(value, start, end, fontFamily, fontSize) {
  const open = `{{f:${fontFamily}|${fontSize}}}`;
  return wrapRangeWithMarkers(value, start, end, open, "{{/f}}");
}

export function applyTextColor(textarea, color) {
  return wrapWithMarkers(textarea, `{{c:${color}}}`, "{{/c}}");
}

export function applyTextColorToRange(value, start, end, color) {
  return wrapRangeWithMarkers(value, start, end, `{{c:${color}}}`, "{{/c}}");
}

export function applyHighlightColor(textarea, color) {
  return wrapWithMarkers(textarea, `{{h:${color}}}`, "{{/h}}");
}

export function applyHighlightColorToRange(value, start, end, color) {
  return wrapRangeWithMarkers(value, start, end, `{{h:${color}}}`, "{{/h}}");
}

export function applyUnderline(textarea) {
  return wrapTextSelection(textarea, "__", "__");
}

export function applyLeftAlign(textarea) {
  return wrapWithMarkers(textarea, "{{a:left}}", "{{/a}}");
}

export const COMMON_EMOJIS = [
  // Faces
  "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃",
  "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙",
  "😋", "😛", "😜", "🤪", "😝", "🤗", "🤭", "🫢", "🤔", "🤫",
  "🫡", "🤐", "😐", "😑", "😶", "😏", "😒", "🙄", "😬", "😌",
  "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕", "🥴", "😵", "🤯",
  "🥳", "🥺", "😞", "😟", "😢", "😭", "😤", "😠", "😡", "🤬",
  "😱", "😨", "😰", "😥", "😓", "🤠", "🤡", "😈", "👿", "💀",
  // Hands / gestures (these follow the selected skin tone)
  "👍", "👎", "👌", "✌️", "🤞", "🤙", "🤟", "🤘", "👏", "🙌",
  "🙏", "🤝", "💪", "👋", "🫶",
  // Hearts & symbols
  "❤️", "🧡", "💛", "💚", "💜", "🖤", "🤍", "💕", "💯", "🔥",
  "✨", "⭐", "🎉", "🎊", "🎂", "🎁", "✅", "❌", "🍕", "☕",
];

// Fitzpatrick skin-tone modifiers. The empty modifier is the default (yellow).
export const SKIN_TONES = [
  { key: "default", label: "Default", modifier: "" },
  { key: "light", label: "Light", modifier: "\u{1F3FB}" },
  { key: "medium-light", label: "Medium-light", modifier: "\u{1F3FC}" },
  { key: "medium", label: "Medium", modifier: "\u{1F3FD}" },
  { key: "medium-dark", label: "Medium-dark", modifier: "\u{1F3FE}" },
  { key: "dark", label: "Dark", modifier: "\u{1F3FF}" },
];

// Hand/person emojis that accept a skin-tone modifier. Stored without the FE0F
// variation selector so the modifier attaches cleanly.
export const SKIN_TONE_EMOJIS = new Set([
  "👍", "👎", "👌", "✌", "🤞", "🤙", "🤟", "🤘", "👏", "🙌",
  "🙏", "🤝", "💪", "👋", "🫶",
]);

// Apply a skin-tone modifier to a supported emoji; others are returned as-is.
export function applySkinTone(emoji, modifier) {
  if (!modifier) return emoji;
  const base = emoji.replace(/\uFE0F/g, "");
  if (!SKIN_TONE_EMOJIS.has(base)) return emoji;
  return base + modifier;
}

// Strip all formatting markup so a message can be shown as plain text (e.g. the
// chat-list last-message preview). Removes block markers ({{...}}), bold/italic/
// underline/strike/code markers, keeps link text, and collapses whitespace.
export function stripMessageMarkup(text = "") {
  return text
    .replace(/\{\{[a-z]:[^}]*\}\}/gi, "") // opening block markers: {{f:..}}, {{c:..}}, {{a:..}}
    .replace(/\{\{\/[a-z]\}\}/gi, "") // closing block markers: {{/f}} etc.
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1") // links -> just the text
    .replace(/(\*\*\*|\*\*|\*|__|_|~~|`)/g, "") // emphasis / code markers
    .replace(/^\s*[-*]\s+/gm, "") // list bullets
    .replace(/^\s*\d+\.\s+/gm, "") // ordered list numbers
    .replace(/^\s*>\s?/gm, "") // blockquotes
    .replace(/\s+/g, " ") // collapse newlines/spaces
    .trim();
}

// Android matches `accept` against MIME types, not extensions: with an
// extension-only list its picker offers the Files/Documents browser alone,
// while iOS still shows Photo Library and Camera. Listing MIME types first
// gets Android the same Camera / Photos / Files choices.
export const CHAT_UPLOAD_ACCEPT =
  "image/jpeg,image/png,image/svg+xml,application/pdf," +
  "application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  ".jpg,.jpeg,.png,.svg,.pdf,.doc,.docx";
export const CHAT_IMAGE_ACCEPT =
  "image/jpeg,image/png,image/svg+xml,.jpg,.jpeg,.png,.svg";

// Android's picker only offers Camera and Photos next to Files when the accept
// list is the broad "image/*" — a list of specific image types still drops the
// user in the Documents browser. iOS shows Photo Library / Take Photo / Choose
// File either way, so only Android needs the looser list; validateChatImageFile
// still refuses anything that isn't JPG/PNG/SVG once a file comes back.
export const CHAT_IMAGE_ACCEPT_ANDROID = "image/*";

const CHAT_UPLOAD_EXTENSIONS = ["jpg", "jpeg", "png", "svg", "pdf", "doc", "docx"];
const CHAT_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "svg"];

const CHAT_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/svg+xml"];

const MIME_TYPE_EXTENSIONS = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/svg+xml": "svg",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
};

const fileExtensionOf = (file) => {
  const name = file?.name || "";
  // "IMG_20260830" (no dot) must not read as an extension of "IMG_20260830" —
  // Android pickers regularly hand back names with no suffix at all.
  if (!name.includes(".")) return "";
  return name.split(".").pop()?.toLowerCase() || "";
};

const fileMimeTypeOf = (file) => (file?.type || "").toLowerCase();

export function validateChatUploadFile(file) {
  if (!file) {
    return { valid: false, message: "No file selected." };
  }
  const ext = fileExtensionOf(file);
  const mime = fileMimeTypeOf(file);
  // Either signal is enough: Android supplies files with no extension, and
  // some content providers report "application/octet-stream" for everything.
  if (MIME_TYPE_EXTENSIONS[mime] || CHAT_UPLOAD_EXTENSIONS.includes(ext)) {
    return { valid: true };
  }
  return {
    valid: false,
    message: "Only JPG, PNG, SVG, Word, and PDF files are allowed.",
  };
}

export function validateChatImageFile(file) {
  if (!file) {
    return { valid: false, message: "No file selected." };
  }
  const ext = fileExtensionOf(file);
  const mime = fileMimeTypeOf(file);
  if (CHAT_IMAGE_MIME_TYPES.includes(mime) || CHAT_IMAGE_EXTENSIONS.includes(ext)) {
    return { valid: true };
  }
  // Android and newer iPhones shoot HEIC — worth naming, since "only JPG/PNG"
  // gives no clue why the photo just picked from the gallery was refused.
  if (mime === "image/heic" || mime === "image/heif" || ext === "heic" || ext === "heif") {
    return {
      valid: false,
      message: "HEIC photos aren't supported. Save the photo as JPG and try again.",
    };
  }
  return {
    valid: false,
    message: "Only JPG, PNG, and SVG images are allowed.",
  };
}

export function getChatAttachmentType(file) {
  // MIME first — an extension-less photo from an Android picker would
  // otherwise be sent as a file card instead of an inline image.
  if (fileMimeTypeOf(file).startsWith("image/")) return "image";
  return CHAT_IMAGE_EXTENSIONS.includes(fileExtensionOf(file)) ? "image" : "file";
}

export function getChatFileExtension(fileName = "", mimeType = "") {
  if (fileName.includes(".")) {
    return fileName.split(".").pop()?.toLowerCase() || "";
  }
  // Fall back to the MIME type for the extension-less names Android hands back.
  return MIME_TYPE_EXTENSIONS[(mimeType || "").toLowerCase()] || "";
}

export function getAttachmentPreviewLabel(messageType, fileName = "") {
  if (messageType === "image") return "Photo";
  return fileName || "File";
}
